// Bedienleiste der Voice-Ansicht, Gerätewahl und die kompakten Knöpfe der Statusleiste.
import type { ScreensharePreset } from '@lagerfeuer/shared';
import { supportsAudioOutputSelection } from 'livekit-client';
import { useEffect, useState } from 'react';
import { getVoiceController } from './controller';
import { CameraIcon, GearIcon, HeadphonesIcon, LeaveIcon, MicIcon, ScreenIcon } from './icons';
import { presetLabel, screenPresetChoice, type VoiceDeviceKind } from './logic';
import { useVoiceStore } from './store';

const run = (p: Promise<unknown>) => {
  p.catch((err: unknown) => console.warn('[voice]', err));
};

/** Stumm/Taub – in Bedienleiste (`voice-…`) und Statusleiste (`voice-status-…`) gleich, nur andere testids. */
export function MuteDeafButtons({ prefix, compact }: { prefix: 'voice' | 'voice-status'; compact?: boolean }) {
  const selfMute = useVoiceStore((s) => s.selfMute);
  const selfDeaf = useVoiceStore((s) => s.selfDeaf);
  const serverMute = useVoiceStore((s) => s.serverMute);
  const serverDeaf = useVoiceStore((s) => s.serverDeaf);
  const canSpeak = useVoiceStore((s) => s.session?.canPublishAudio ?? true);
  const busy = useVoiceStore((s) => !!s.busy.mic);
  const cls = compact ? 'lf-voice-iconbtn' : 'lf-voice-btn';
  const muteLabel = serverMute
    ? 'Von einem Moderator stummgeschaltet'
    : !canSpeak
      ? 'Sprechen ist hier nicht erlaubt'
      : selfMute
        ? 'Stummschaltung aufheben'
        : 'Stummschalten';
  const deafOff = selfDeaf || serverDeaf;
  const deafLabel = serverDeaf ? 'Von einem Moderator taubgeschaltet' : selfDeaf ? 'Taubschaltung aufheben' : 'Taubschalten';
  return (
    <>
      <button
        type="button"
        className={`${cls}${selfMute || !canSpeak || serverMute ? ' is-off' : ''}`}
        data-testid={`${prefix}-mute`}
        data-server-muted={serverMute ? 'true' : undefined}
        aria-pressed={selfMute}
        aria-label={muteLabel}
        title={muteLabel}
        disabled={!canSpeak || busy}
        onClick={() => run(getVoiceController().toggleMute())}
      >
        <MicIcon off={selfMute || !canSpeak || serverMute} />
      </button>
      <button
        type="button"
        className={`${cls}${deafOff ? ' is-off' : ''}`}
        data-testid={`${prefix}-deafen`}
        data-server-deafened={serverDeaf ? 'true' : undefined}
        aria-pressed={selfDeaf}
        aria-label={deafLabel}
        title={deafLabel}
        onClick={() => run(getVoiceController().toggleDeaf())}
      >
        <HeadphonesIcon off={deafOff} />
      </button>
    </>
  );
}

function ScreenPresetSelect() {
  const preset = useVoiceStore((s) => s.screenPreset);
  const max = useVoiceStore((s) => s.session?.screenshareMaxPreset);
  const disabled = useVoiceStore((s) => !s.session?.canScreenshare || !!s.busy.screen);
  const { allowed } = screenPresetChoice(max, preset);
  return (
    <label className="lf-voice-select lf-voice-select--inline" title="Qualität beim Bildschirmteilen">
      <span className="lf-voice-sr">Qualität beim Bildschirmteilen</span>
      <select
        data-testid="voice-screenshare-quality"
        value={preset}
        disabled={disabled}
        onChange={(e) => run(getVoiceController().setScreenPreset(e.currentTarget.value as ScreensharePreset))}
      >
        {allowed.map((p) => (
          <option key={p} value={p}>
            {presetLabel(p)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function VoiceControls() {
  const camera = useVoiceStore((s) => s.camera);
  const screen = useVoiceStore((s) => s.screen);
  const canVideo = useVoiceStore((s) => !!s.session?.canPublishVideo);
  const canScreen = useVoiceStore((s) => !!s.session?.canScreenshare);
  const busyCam = useVoiceStore((s) => !!s.busy.camera);
  const busyScreen = useVoiceStore((s) => !!s.busy.screen);
  const [settings, setSettings] = useState(false);

  const camLabel = !canVideo ? 'Video ist hier nicht erlaubt' : camera ? 'Kamera ausschalten' : 'Kamera einschalten';
  const screenLabel = !canScreen ? 'Bildschirmteilen ist hier nicht erlaubt' : screen ? 'Teilen beenden' : 'Bildschirm teilen';

  return (
    <div className="lf-voice-controls-wrap">
      {settings ? <DeviceSettings onClose={() => setSettings(false)} /> : null}
      <div className="lf-voice-controls" role="toolbar" aria-label="Sprachsteuerung">
        <MuteDeafButtons prefix="voice" />
        <button
          type="button"
          className={`lf-voice-btn${camera ? ' is-on' : ''}`}
          data-testid="voice-camera"
          aria-pressed={camera}
          aria-label={camLabel}
          title={camLabel}
          disabled={!canVideo || busyCam}
          onClick={() => run(getVoiceController().toggleCamera())}
        >
          <CameraIcon off={!camera} />
        </button>
        <span className="lf-voice-split">
          <button
            type="button"
            className={`lf-voice-btn${screen ? ' is-on' : ''}`}
            data-testid="voice-screenshare"
            aria-pressed={screen}
            aria-label={screenLabel}
            title={screenLabel}
            disabled={!canScreen || busyScreen}
            onClick={() => run(getVoiceController().toggleScreen())}
          >
            <ScreenIcon active={screen} />
          </button>
          <ScreenPresetSelect />
        </span>
        <button
          type="button"
          className={`lf-voice-btn${settings ? ' is-on' : ''}`}
          data-testid="voice-settings"
          aria-pressed={settings}
          aria-expanded={settings}
          aria-label="Geräte-Einstellungen"
          title="Geräte-Einstellungen"
          onClick={() => setSettings((v) => !v)}
        >
          <GearIcon />
        </button>
        <button
          type="button"
          className="lf-voice-btn lf-voice-btn--danger"
          data-testid="voice-leave"
          aria-label="Verbindung trennen"
          title="Verbindung trennen"
          onClick={() => run(getVoiceController().leave())}
        >
          <LeaveIcon />
        </button>
      </div>
    </div>
  );
}

const KIND_LABEL: Record<VoiceDeviceKind, string> = { audioinput: 'Mikrofon', videoinput: 'Kamera', audiooutput: 'Ausgabe' };

function DeviceSelect({ kind }: { kind: VoiceDeviceKind }) {
  const devices = useVoiceStore((s) => s.devices[kind]);
  const active = useVoiceStore((s) => s.activeDevices[kind] ?? '');
  const known = devices.some((d) => d.deviceId === active);
  return (
    <label className="lf-voice-select">
      <span>{KIND_LABEL[kind]}</span>
      <select
        data-testid={`voice-device-${kind}`}
        value={known ? active : ''}
        disabled={devices.length === 0}
        onChange={(e) => run(getVoiceController().switchDevice(kind, e.currentTarget.value))}
      >
        {!known ? <option value="">{devices.length === 0 ? 'Keine Geräte gefunden' : 'Standard'}</option> : null}
        {devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function DeviceSettings({ onClose }: { onClose: () => void }) {
  const bitrate = useVoiceStore((s) => s.session?.bitrate ?? 0);
  useEffect(() => {
    // Beim Öffnen Geräte (mit Namen) abfragen.
    run(getVoiceController().refreshDevices());
  }, []);
  return (
    <div className="lf-voice-settings" data-testid="voice-device-settings" role="group" aria-label="Geräte">
      <DeviceSelect kind="audioinput" />
      <DeviceSelect kind="videoinput" />
      {supportsAudioOutputSelection() ? <DeviceSelect kind="audiooutput" /> : null}
      <p className="lf-voice-hint">Audio-Bitrate: {Math.round(bitrate / 1000)} kbit/s</p>
      <button type="button" className="lf-voice-linkbtn" onClick={onClose} data-testid="voice-settings-close">
        Schließen
      </button>
    </div>
  );
}

export function LeaveButtonCompact() {
  return (
    <button
      type="button"
      className="lf-voice-iconbtn lf-voice-iconbtn--danger"
      data-testid="voice-status-leave"
      aria-label="Verbindung trennen"
      title="Verbindung trennen"
      onClick={() => run(getVoiceController().leave())}
    >
      <LeaveIcon />
    </button>
  );
}
